// ─── AdminSignInModal ─────────────────────────────────────────────────────────
// Re-authentication prompt shown before the dashboard enters Edit Mode, per
// the XD "Admin Sign In" frame: Email (prefilled with the signed-in account),
// Password with a show/hide eye, a full-width CONTINUE button and a Cancel
// link beneath it.
//
// This confirms the person at the keyboard is the account holder before they
// restructure a dashboard — it is NOT an authorisation step. Permission to
// edit is still decided by the existing project-role check; this modal only
// runs after that check has already passed, so it can never grant an ability
// the user did not already have.
//
// The password is posted to /api/account/verify-password/ and never stored,
// logged, or held after the modal closes.

import { useEffect, useRef, useState } from 'react'
import { verifyPassword } from '../../lib/accountApi'

const EyeIcon = ({ off }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    {off && <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />}
  </svg>
)

export function AdminSignInModal({ open, email = '', onCancel, onSuccess }) {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const passwordRef = useRef(null)

  // Reset on every open so a previous attempt's password or error never
  // lingers in a reopened dialog.
  useEffect(() => {
    if (!open) return
    setPassword('')
    setShowPassword(false)
    setError(null)
    setSubmitting(false)
    const id = setTimeout(() => passwordRef.current?.focus(), 50)
    return () => clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const submit = async (e) => {
    e?.preventDefault?.()
    if (submitting || !password) return
    setSubmitting(true)
    setError(null)
    try {
      await verifyPassword({ email, password })
      setPassword('')          // drop it as soon as it has served its purpose
      onSuccess?.()
    } catch (err) {
      setError(err?.message || 'Incorrect email or password.')
      setPassword('')
      passwordRef.current?.focus()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Admin Sign In"
        className="w-full max-w-[520px] bg-white rounded-md shadow-xl"
      >
        <div className="px-8 pt-7 pb-2">
          <h2 className="text-xl font-bold text-[#4D4D4D]">Admin Sign In</h2>
        </div>
        <div className="border-t border-gray-200" />

        <form onSubmit={submit} className="px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <label htmlFor="admin-signin-email" className="w-24 shrink-0 text-[15px] font-bold text-[#4D4D4D]">
              Email
            </label>
            <input
              id="admin-signin-email"
              type="email"
              value={email}
              readOnly
              // Read-only on purpose: the endpoint only ever re-checks the
              // SIGNED-IN account, so an editable field would imply you could
              // authorise as somebody else.
              className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 text-sm text-[#4D4D4D] bg-gray-50"
            />
          </div>

          <div className="flex items-center gap-4">
            <label htmlFor="admin-signin-password" className="w-24 shrink-0 text-[15px] font-bold text-[#4D4D4D]">
              Password
            </label>
            <div className="flex-1 min-w-0 relative">
              <input
                id="admin-signin-password"
                ref={passwordRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(null) }}
                placeholder="Password"
                autoComplete="current-password"
                disabled={submitting}
                className="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm text-[#4D4D4D]
                           focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-4 text-sm text-red-600 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting || !password}
            className="mt-7 w-full py-3 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-[15px] font-bold
                       tracking-wide rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'CHECKING…' : 'CONTINUE'}
          </button>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-[#4D4D4D] underline hover:text-[#358ECD]"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
